import React from 'react';
import { Checkbox, LabelInput } from '@/components/ui/inputs';
import { Spinner } from '@/components/ui/loading';
import { Text } from '@/components/ui/text/Text';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';


export interface PanelSideConfig<T> {
  title: string;
  items: T[];
  allItems: T[];
  isLoading: boolean;
  totalRecords: number;
  searchValue: string;
  onSearch: (text: string) => void;
  searchPlaceholder?: string;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onDoubleClick: (item: T) => void;
  onBulkAction?: () => void;
  bulkActionMessage?: string;
  sentinelRef: React.RefCallback<HTMLDivElement>;
  scrollContainerRef: React.RefCallback<HTMLDivElement>;
  selectAllHint?: string;
  footerText: string;
  'data-testid'?: string;
}

export interface TransferPanelProps<T> {
  keyExtractor: (item: T) => number;
  renderTitle: (item: T) => string;
  renderSubtitle?: (item: T) => string;
  leftPanel: PanelSideConfig<T>;
  rightPanel: PanelSideConfig<T>;
  onAdd: () => void;
  onRemove: () => void;
  addLabel?: string;
  removeLabel?: string;
  addAllLabel?: string;
  removeAllLabel?: string;
  isAddDisabled: boolean;
  isRemoveDisabled: boolean;
  /** When true, hides the center Add/Remove action column (e.g. read-only users). */
  hideActions?: boolean;
  'data-testid'?: string;
}

function PanelSide<T>({
  config,
  keyExtractor,
  renderTitle,
  renderSubtitle,
  bulkLabel,
  direction,
}: {
  config: PanelSideConfig<T>;
  keyExtractor: (item: T) => number;
  renderTitle: (item: T) => string;
  renderSubtitle?: (item: T) => string;
  bulkLabel: string;
  direction: 'add' | 'remove';
}) {
  const {
    title, items, allItems, isLoading, totalRecords, searchValue,
    onSearch, searchPlaceholder, selectedIds, onToggleSelect, onSelectAll,
    isAllSelected, isIndeterminate, onDoubleClick, onBulkAction,
    bulkActionMessage, sentinelRef, scrollContainerRef,
    selectAllHint, footerText, 'data-testid': dataTestId,
  } = config;

  const hasMoreOnServer = allItems.length < totalRecords;

  return (
    <div
      className="flex flex-col border border-gray-200 rounded-2xl overflow-hidden"
      data-testid={dataTestId}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
        <div title={selectAllHint}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={onSelectAll}
            data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
          />
        </div>
        <Text variant="subheader-medium" color={colors.grays.neutral66} className="flex-1">
          {title}
        </Text>
        <div className="w-48">
          <LabelInput
            label=""
            aria-label={searchPlaceholder ?? title}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(value) => onSearch(value)}
            data-testid={dataTestId ? `${dataTestId}-search` : undefined}
          />
        </div>
      </div>

      {/* Bulk action banner */}
      {hasMoreOnServer && onBulkAction && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-blue-50 border-b border-blue-100">
          <Text variant="small-regular" color={colors.primary.main}>
            {bulkActionMessage}
          </Text>
          <PrimaryButton
            variant="outline"
            onClick={onBulkAction}
            className="shrink-0 text-xs"
            iconRight={direction === 'add' ? <IconScout name="arrowRight" /> : undefined}
            iconLeft={direction === 'remove' ? <IconScout name="arrowLeft" /> : undefined}
            data-testid={dataTestId ? `${dataTestId}-bulk-action` : undefined}
          >
            {bulkLabel}
          </PrimaryButton>
        </div>
      )}

      {/* Item list */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto min-h-48 max-h-96"
      >
        {isLoading && items.length === 0 ? (
          <div className="flex justify-center items-center h-24">
            <Spinner />
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const id = keyExtractor(item);
              return (
                <li
                  key={id}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer select-none"
                  onDoubleClick={() => onDoubleClick(item)}
                  data-testid={dataTestId ? `${dataTestId}-item-${id}` : undefined}
                >
                  <Checkbox
                    checked={selectedIds.has(id)}
                    onChange={() => onToggleSelect(id)}
                    data-testid={dataTestId ? `${dataTestId}-check-${id}` : undefined}
                  />
                  <div className="flex flex-col min-w-0">
                    <Text variant="subheader-regular" color={colors.primary.main}>
                      {renderTitle(item)}
                    </Text>
                    {renderSubtitle && (
                      <Text variant="small-regular" color={colors.grays.neutral66}>
                        {renderSubtitle(item)}
                      </Text>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {isLoading && items.length > 0 && (
          <div className="flex justify-center py-2">
            <Spinner />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-right">
        <Text variant="small-regular" color={colors.grays.neutral66}>
          {footerText}
        </Text>
      </div>
    </div>
  );
}

export function TransferPanel<T>({
  keyExtractor,
  renderTitle,
  renderSubtitle,
  leftPanel,
  rightPanel,
  onAdd,
  onRemove,
  addLabel,
  removeLabel,
  addAllLabel,
  removeAllLabel,
  isAddDisabled,
  isRemoveDisabled,
  hideActions = false,
  'data-testid': dataTestId,
}: TransferPanelProps<T>) {
  const { t: translate } = useTranslation();

  const resolvedAddLabel = addLabel ?? translate('common:actions.add');
  const resolvedRemoveLabel = removeLabel ?? translate('common:actions.remove');
  const resolvedAddAllLabel = addAllLabel ?? translate('common:actions.addAll');
  const resolvedRemoveAllLabel = removeAllLabel ?? translate('common:actions.removeAll');

  return (
    <div className="flex items-start gap-4" data-testid={dataTestId}>
      <div className="flex-[42]">
        <PanelSide
          config={leftPanel}
          keyExtractor={keyExtractor}
          renderTitle={renderTitle}
          renderSubtitle={renderSubtitle}
          bulkLabel={resolvedAddAllLabel}
          direction="add"
        />
      </div>

      {!hideActions && (
        <div className="flex-[14] flex flex-col items-center justify-start gap-3 pt-12">
          <PrimaryButton
            variant="outline"
            onClick={onAdd}
            disabled={isAddDisabled}
            iconRight={<IconScout name="arrowRight" />}
            data-testid={dataTestId ? `${dataTestId}-add-button` : undefined}
            className="w-32 text-sm"
          >
            {resolvedAddLabel}
          </PrimaryButton>
          <PrimaryButton
            variant="outline"
            onClick={onRemove}
            disabled={isRemoveDisabled}
            iconLeft={<IconScout name="arrowLeft" />}
            data-testid={dataTestId ? `${dataTestId}-remove-button` : undefined}
            className="w-32 text-sm"
          >
            {resolvedRemoveLabel}
          </PrimaryButton>
        </div>
      )}

      <div className="flex-[42]">
        <PanelSide
          config={rightPanel}
          keyExtractor={keyExtractor}
          renderTitle={renderTitle}
          renderSubtitle={renderSubtitle}
          bulkLabel={resolvedRemoveAllLabel}
          direction="remove"
        />
      </div>
    </div>
  );
}

export default TransferPanel;

